/**
 * BprDiagramCanvas - renders one BPR diagram spec (system architecture or
 * functional workflow) as an interactive React Flow canvas.
 *
 * The spec is topology only (the LLM never authors coordinates); layout runs
 * here with elkjs - 'layered' down for system architecture, left-to-right
 * for workflows - via the shared flowLayout helper. Node styling maps
 * `status` (existing solid / new highlighted / changed dashed / removed
 * muted) and edges show their flow labels (manual handoffs dashed). Clicking
 * a node with issues opens the linked issues in the section's side panel
 * (via onSelectNode). Handles follow the layout direction so edges attach
 * top/bottom on top-down diagrams and left/right on horizontal ones.
 * System-architecture boxes carry the system's logo (or initials), like the
 * Systems Landscape cards - see diagramNodeIcon.
 */

import '@xyflow/react/dist/style.css';

import type { BprDiagramNode as BprDiagramSpecNode, BprDiagramSpec } from '@protoss/shared';
import { Button } from '@protoss/ui';
import {
  Background,
  Handle,
  Panel,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import * as React from 'react';
import {
  BPR_DIAGRAM_PDF_LEGEND,
  computeFlowCapture,
  downloadDiagramPdf,
} from '../utils/diagramPdf';
import { diagramNodeIcon, type DiagramNodeIcon } from '../utils/diagramNodeIcon';
import { DiagramNodeIconBadge } from './DiagramNodeIconBadge';
import { layoutElkGraph, type FlowDirection } from '../utils/flowLayout';

// Fixed footprint (keeps the elk layout truthful): two clamped label lines
// plus the meta row.
const NODE_WIDTH = 216;
const NODE_HEIGHT = 78;

type DiagramFlowNode = Node<
  { spec: BprDiagramSpecNode; direction: FlowDirection; icon: DiagramNodeIcon | null },
  'bprDiagramNode'
>;

function BprDiagramFlowNode({ data, selected }: NodeProps<DiagramFlowNode>) {
  const spec = data.spec;
  const vertical = data.direction === 'DOWN';
  const classes = [
    'bpr-diagram-node',
    `bpr-diagram-node--${spec.status}`,
    `bpr-diagram-node--type-${spec.type}`,
    selected ? 'bpr-diagram-node--selected' : '',
    spec.issues.length > 0 ? 'bpr-diagram-node--has-issues' : '',
    data.icon ? 'bpr-diagram-node--with-icon' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes}>
      <Handle
        type="target"
        position={vertical ? Position.Top : Position.Left}
        className="bpr-diagram-node__handle"
      />
      {data.icon ? <DiagramNodeIconBadge icon={data.icon} /> : null}
      <div className="bpr-diagram-node__body">
        <div className="bpr-diagram-node__label" title={spec.label}>
          {spec.label}
        </div>
        <div className="bpr-diagram-node__meta">
          <span className="bpr-diagram-node__system">{spec.system}</span>
          {spec.issues.length > 0 ? (
            <span className="bpr-diagram-node__issue-count">
              {spec.issues.length} issue{spec.issues.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
      </div>
      <Handle
        type="source"
        position={vertical ? Position.Bottom : Position.Right}
        className="bpr-diagram-node__handle"
      />
    </div>
  );
}

const NODE_TYPES = { bprDiagramNode: BprDiagramFlowNode };

export interface BprDiagramCanvasProps {
  spec: BprDiagramSpec;
  /** Fires with the clicked node (null on background click). */
  onSelectNode?: (node: BprDiagramSpecNode | null) => void;
  /**
   * Title for the per-diagram "PDF" download (top-right panel). Every
   * generated diagram surface passes it; omit to hide the affordance.
   */
  exportTitle?: string;
}

export function BprDiagramCanvas({ spec, onSelectNode, exportTitle }: BprDiagramCanvasProps) {
  const [nodes, setNodes] = React.useState<DiagramFlowNode[]>([]);
  const [edges, setEdges] = React.useState<Edge[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  const handleExportPdf = React.useCallback(async () => {
    if (!containerRef.current || !exportTitle || isExporting || nodes.length === 0) return;
    setIsExporting(true);
    try {
      const capture = computeFlowCapture(containerRef.current, nodes);
      await downloadDiagramPdf(containerRef.current, exportTitle, capture, {
        legend: BPR_DIAGRAM_PDF_LEGEND,
      });
    } catch {
      // Best-effort: a failed capture leaves the canvas untouched.
    } finally {
      setIsExporting(false);
    }
  }, [exportTitle, isExporting, nodes]);

  React.useEffect(() => {
    let cancelled = false;
    const direction: FlowDirection = spec.kind === 'workflow' ? 'RIGHT' : 'DOWN';
    void layoutElkGraph(
      spec.nodes.map((n) => ({ id: n.id, width: NODE_WIDTH, height: NODE_HEIGHT })),
      spec.edges.map((e) => ({ from: e.from, to: e.to })),
      direction
    ).then((positions) => {
      if (cancelled) return;
      setNodes(
        spec.nodes.map((n) => ({
          id: n.id,
          type: 'bprDiagramNode' as const,
          position: positions.get(n.id) ?? { x: 0, y: 0 },
          data: { spec: n, direction, icon: diagramNodeIcon(n, spec.kind) },
          // Fixed footprint keeps the elk layout truthful.
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
        }))
      );
      setEdges(
        spec.edges.map((e, i) => ({
          id: `e${i}`,
          source: e.from,
          target: e.to,
          label: e.label,
          type: 'smoothstep',
          labelBgPadding: [6, 3] as [number, number],
          labelBgBorderRadius: 4,
          className: [
            'bpr-diagram-edge',
            `bpr-diagram-edge--${e.kind}`,
            `bpr-diagram-edge--status-${e.status}`,
          ].join(' '),
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [spec]);

  return (
    <div className="bpr-diagram-canvas" ref={containerRef}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.12, maxZoom: 1 }}
        minZoom={0.2}
        nodesDraggable
        nodesConnectable={false}
        edgesFocusable={false}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_, node) => onSelectNode?.((node as DiagramFlowNode).data.spec)}
        onPaneClick={() => onSelectNode?.(null)}
        aria-label={spec.title}
      >
        <Background gap={24} />
        {exportTitle ? (
          <Panel position="top-right">
            {/* data-pdf-exclude keeps the button itself out of the capture. */}
            <div data-pdf-exclude="true">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isExporting}
                onClick={() => void handleExportPdf()}
              >
                {isExporting ? 'Exporting…' : 'Download PDF'}
              </Button>
            </div>
          </Panel>
        ) : null}
      </ReactFlow>
    </div>
  );
}
